import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import {
  MID,
  NOT_WIDE,
  TINY,
  marketingPalette,
} from "@/components/marketing/tokens";
import { localSrcsetFor } from "@/components/marketing/localSrcset";
import { SUPPORT } from "./homeContent";
import { BODY_SMALL, HEADING_2 } from "./homeType";

/**
 * The left-aligned heading every block below the stat band shares. Two of them
 * on the page, so it lives with the first one that needs it.
 */
export function WhyBlockHeading({
  heading,
  body,
}: {
  heading: string;
  body: string;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "8px",
        width: 1,
        maxWidth: "25.3rem",
        textAlign: "left",
      }}
    >
      <Typography component="h2" sx={HEADING_2}>
        {heading}
      </Typography>
      <Typography component="p" sx={BODY_SMALL}>
        {body}
      </Typography>
    </Box>
  );
}

/**
 * The block's own frame: heading above, grid below, left-aligned.
 *
 * The 2rem gutter is the block's, not the page's, the container above it
 * already stops at 1360, and without this the cards run to that container's
 * edge while the published block insets them.
 */
export const WHY_BLOCK = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "32px",
  px: "2rem",
  // Below the card step the published block runs to the page's own gutter.
  [MID]: { gap: "24px", px: 0 },
};

export default function WhySupport() {
  return (
    <Box sx={WHY_BLOCK}>
      <WhyBlockHeading heading={SUPPORT.heading} body={SUPPORT.body} />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(12, 1fr)",
          gap: "16px",
          width: 1,
          [NOT_WIDE]: { gridTemplateColumns: "repeat(8, 1fr)" },
          [MID]: { display: "flex", flexDirection: "column" },
        }}
      >
        {SUPPORT.cards.map((card) => (
          <Box
            key={card.title}
            sx={{
              gridColumn: "span 4",
              display: "flex",
              flexDirection: "column",
              gap: "24px",
              p: "16px",
              borderRadius: "16px",
              backgroundColor: marketingPalette.featureCard,
            }}
          >
            <Box
              sx={{
                position: "relative",
                // The card's picture is aspect-locked by its own padding, which
                // is what keeps the three cards level before the images decode.
                pt: "90%",
                borderRadius: "12px",
                backgroundColor: "background.default",
                overflow: "hidden",
                boxShadow: "0 4px 13px -6px rgba(93, 97, 138, 0.22)",
              }}
            >
              <Box
                component="img"
                src={card.image}
                srcSet={
                  card.srcsetWidth
                    ? localSrcsetFor(card.image, card.srcsetWidth)
                    : ""
                }
                sizes="100vw"
                alt=""
                loading="lazy"
                sx={{
                  position: "absolute",
                  inset: 0,
                  width: 1,
                  height: 1,
                  objectFit: "cover",
                }}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "4.8px",
                width: 1,
              }}
            >
              <Box
                sx={{
                  color: marketingPalette.ink,
                  fontSize: 17,
                  lineHeight: "150%",
                  WebkitTextStrokeWidth: "0.7px",
                  [MID]: { fontSize: 16, lineHeight: "24px" },
                  [TINY]: { fontSize: 15, lineHeight: "21px" },
                }}
              >
                {card.title}
              </Box>
              <Typography
                component="p"
                sx={{
                  color: marketingPalette.inkMuted,
                  fontSize: "0.88rem",
                  lineHeight: "150%",
                }}
              >
                {card.body}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
